import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ApiError, api, type BoardQueue, type DisplayOptions, type TextAgent, type TextBoard, type TextNumbers, type TextTeamBoard, type Wallboard, type WallboardDisplay, type WaitingText } from '../../api/client';
import { agentTotals } from '../../lib/agents';
import { BOARD_SOUNDS, DEFAULT_SOUNDS, VOLUMES, previewSound, useBoardSounds, type BoardSound, type SoundVolume } from '../../lib/boardSounds';
import { useTenant } from '../../components/Shell';
import { zoneName } from '../../lib/time';
import { handToWords } from '../../lib/textRouting';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { clockTime, plural, shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';
import { Icon } from '../../lib/icons';
import { useAuth } from '../../lib/auth';
import { TurnOffDnd, useCanTurnOffDnd } from '../../components/DndOff';
// Texts on the wallboard (1.12.32): only for ring groups and queues that take texts.
import { TEXT_LIMIT_SECONDS, TEXT_LIMIT_WAITING, agentTextWords, loadTextLimits, oldestNow, saveTextLimits, textTones, type TextLimits } from '../../lib/textBoard';

export const REFRESH_MS = 5000;

/** When a number should catch the eye. Kept per browser (each TV can differ). */
export interface Limits {
  waiting: number;
  waitSeconds: number;
}
const DEFAULT_LIMITS: Limits = { waiting: 3, waitSeconds: 120 };
const LIMITS_KEY = 'stitch.wallboard.limits';

export function loadLimits(): Limits {
  try {
    const raw = JSON.parse(localStorage.getItem(LIMITS_KEY) ?? 'null') as Partial<Limits> | null;
    return { waiting: Number(raw?.waiting) || DEFAULT_LIMITS.waiting, waitSeconds: Number(raw?.waitSeconds) || DEFAULT_LIMITS.waitSeconds };
  } catch {
    return DEFAULT_LIMITS;
  }
}
function saveLimits(l: Limits) {
  try {
    localStorage.setItem(LIMITS_KEY, JSON.stringify(l));
  } catch {
    /* private mode: fine for this session */
  }
}

/**
 * Sounds on this computer's wallboard page (TVs have their own, set per TV). Off unless turned on.
 * texts: a chime when a text starts waiting for a ring group or queue (1.12.33), off unless chosen.
 */
interface PageSounds {
  on: boolean;
  volume: SoundVolume;
  texts: boolean;
}
const SOUNDS_KEY = 'stitch.wallboard.sounds';
function loadPageSounds(): PageSounds {
  try {
    const raw = JSON.parse(localStorage.getItem(SOUNDS_KEY) ?? 'null') as Partial<PageSounds> | null;
    const volume = raw?.volume === 'low' || raw?.volume === 'high' ? raw.volume : 'medium';
    return { on: raw?.on === true, volume, texts: raw?.texts === true };
  } catch {
    return { on: false, volume: 'medium', texts: false };
  }
}
const WITH_TEXTS: BoardSound[] = [...DEFAULT_SOUNDS, 'texts'];
function savePageSounds(s: PageSounds) {
  try {
    localStorage.setItem(SOUNDS_KEY, JSON.stringify(s));
  } catch {
    /* private mode: fine for this session */
  }
}

const LIMIT_WAITING = [1, 2, 3, 4, 5, 8, 10];
const LIMIT_SECONDS = [30, 60, 120, 180, 300, 600];

/** "0:42", "12:05". */
export function mmss(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`;
}

export function toneFor(value: number, limit: number): 'calm' | 'warn' | 'crit' {
  if (value >= limit * 2) return 'crit';
  if (value >= limit) return 'warn';
  return 'calm';
}

export function TenantWallboard() {
  const { tenant } = useTenant();
  const [board, setBoard] = useState<(Wallboard & { canSignIn?: 'all' | string[] }) | null>(null);
  const [signing, setSigning] = useState('');
  const [signProblem, setSignProblem] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [paused, setPaused] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [limits, setLimits] = useState<Limits>(loadLimits);
  const [settings, setSettings] = useState(false);
  const [tvOpen, setTvOpen] = useState(false);
  const [pageSounds, setPageSounds] = useState<PageSounds>(loadPageSounds);
  const [texts, setTexts] = useState<TextBoard | null>(null);
  const [textLimits, setTextLimits] = useState<TextLimits>(loadTextLimits);
  const displays = useData(() => api.wallboardDisplays(tenant.domain).catch(() => ({ displays: [] as WallboardDisplay[] })), [tenant.domain]);
  const root = useRef<HTMLDivElement>(null);
  // Admins (and Stitch staff) can turn off an agent's do not disturb from the board.
  const canDndOff = useCanTurnOffDnd(tenant.domain) && tenant.writable;

  const load = useCallback(async () => {
    try {
      // Texts alongside: a board without them (an older server, a hiccup) still shows calls.
      const [b, t] = await Promise.all([api.wallboard(tenant.domain), api.wallboardTexts(tenant.domain).catch(() => null)]);
      setBoard(b);
      setTexts(t);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, [tenant.domain]);

  // Refresh every few seconds while the page is visible and not paused.
  useEffect(() => {
    void load();
    if (paused) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load, paused]);

  // Waits tick up between refreshes.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);


  const notYet = error instanceof ApiError && error.code === 'NOT_YET';
  const since = board ? Math.max(0, (now - Date.parse(board.at)) / 1000) : 0;
  const age = board ? (since < 2 ? 'just now' : `${Math.round(since)} s ago`) : '';
  const stale = since > 30 && !paused;
  const sounds = useBoardSounds(
    paused ? null : board,
    { ...pageSounds, on: pageSounds.on && !paused, events: pageSounds.texts ? WITH_TEXTS : DEFAULT_SOUNDS },
    limits,
    since,
    Boolean(error) || stale,
    texts?.teams ?? null
  );
  const setSounds = (next: PageSounds) => {
    setPageSounds(next);
    savePageSounds(next);
    if (next.on) void sounds.unlock();
  };

  const totals = board ? boardTotals(board.queues) : undefined;
  const textTeams = texts?.teams ?? [];
  const textOf = new Map(textTeams.map((t) => [t.extension, t]));
  // Ring groups (and any queue the board didn't list) that take texts: a tile of their own.
  const textOnly = board ? textTeams.filter((t) => !board.queues.some((q) => q.extension === t.extension)) : [];

  return (
    <div ref={root} className="wallboard">
      {(
        <PageHeader
          eyebrow={`Contact Center · ${tenant.domain}`}
          title="Wallboard"
          actions={
            <>
              <span className={`wb-age${stale ? ' stale' : ''}`} aria-live="off">
                {paused ? 'Paused' : board ? `Updated ${age}` : ''}
              </span>
              <Button variant="ghost" icon={paused ? 'play' : 'pause'} onClick={() => setPaused((p) => !p)}>
                {paused ? 'Resume' : 'Pause'}
              </Button>
              <Button variant="ghost" icon="sliders" onClick={() => setSettings((s) => !s)} aria-expanded={settings}>
                Alerts
              </Button>
              <Button variant="primary" icon="tv" onClick={() => setTvOpen(true)} disabled={!board}>
                Open on a TV
              </Button>
            </>
          }
        />
      )}
      <div className="content">

        {settings && (
          <Card title="When numbers turn amber">
            <div className="wb-settings">
              <label className="field">
                <span className="field-label">Callers waiting</span>
                <select
                  className="input"
                  value={limits.waiting}
                  onChange={(e) => {
                    const l = { ...limits, waiting: Number(e.target.value) };
                    setLimits(l);
                    saveLimits(l);
                  }}
                >
                  {LIMIT_WAITING.map((n) => (
                    <option key={n} value={n}>
                      {n} or more
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field-label">Longest wait</span>
                <select
                  className="input"
                  value={limits.waitSeconds}
                  onChange={(e) => {
                    const l = { ...limits, waitSeconds: Number(e.target.value) };
                    setLimits(l);
                    saveLimits(l);
                  }}
                >
                  {LIMIT_SECONDS.map((n) => (
                    <option key={n} value={n}>
                      {mmss(n)} or longer
                    </option>
                  ))}
                </select>
              </label>
              {textTeams.length > 0 && (
                <>
                  <label className="field">
                    <span className="field-label">Texts waiting</span>
                    <select
                      className="input"
                      value={textLimits.waiting}
                      onChange={(e) => {
                        const l = { ...textLimits, waiting: Number(e.target.value) };
                        setTextLimits(l);
                        saveTextLimits(l);
                      }}
                    >
                      {TEXT_LIMIT_WAITING.map((n) => (
                        <option key={n} value={n}>
                          {n} or more
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span className="field-label">Oldest text</span>
                    <select
                      className="input"
                      value={textLimits.waitSeconds}
                      onChange={(e) => {
                        const l = { ...textLimits, waitSeconds: Number(e.target.value) };
                        setTextLimits(l);
                        saveTextLimits(l);
                      }}
                    >
                      {TEXT_LIMIT_SECONDS.map((n) => (
                        <option key={n} value={n}>
                          {mmss(n)} or longer
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              <span className="hint">They turn red at twice that. Saved on this computer only, so each screen can have its own.</span>
            </div>
            <div className="wb-settings wb-sound-settings">
              <label className="check-row">
                <input type="checkbox" checked={pageSounds.on} onChange={(e) => setSounds({ ...pageSounds, on: e.target.checked })} />
                <span className="cell-stack">
                  <span>Play sounds on this computer</span>
                  <small className="muted">A different sound when a caller starts waiting, a queue turns amber or red, nobody is ready, or a caller hangs up. TVs have their own (Settings, by each TV).</small>
                </span>
              </label>
              {pageSounds.on && (
                <label className="field">
                  <span className="field-label">Volume</span>
                  <select className="input" value={pageSounds.volume} onChange={(e) => setSounds({ ...pageSounds, volume: e.target.value as SoundVolume })}>
                    {VOLUMES.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {pageSounds.on && textTeams.length > 0 && (
                <div className="check-row sound-choice">
                  <label className="check-row">
                    <input type="checkbox" checked={pageSounds.texts} onChange={(e) => setSounds({ ...pageSounds, texts: e.target.checked })} />
                    <span className="cell-stack">
                      <span>Also when a text starts waiting</span>
                      <small className="muted">Two quick high notes when a ring group or queue gets a text nobody has answered yet.</small>
                    </span>
                  </label>
                  <button type="button" className="link-btn small" onClick={() => void previewSound('texts', pageSounds.volume)} aria-label="Play: A text starts waiting">
                    <Icon name="play" size={13} /> Play
                  </button>
                </div>
              )}
              {sounds.needsClick && <span className="hint">Click anywhere on the page to let it play sound.</span>}
            </div>
          </Card>
        )}

        {error && !board ? (
          notYet ? (
            <Card>
              <Empty title="The wallboard is being set up">It will appear here soon. In the meantime, queue activity shows under Call history.</Empty>
            </Card>
          ) : (
            <Card>
              <LoadError error={error} retry={() => void load()} />
            </Card>
          )
        ) : !board ? (
          <Card>
            <Loading label="Loading the wallboard" />
          </Card>
        ) : board.queues.length === 0 && textTeams.length === 0 ? (
          <Card>
            <Empty title="No call queues yet">
              Add a call queue, and its live numbers appear here. <Link to="../queues">Go to Call Queues</Link>
            </Empty>
          </Card>
        ) : (
          <>
            {error && <Notice tone="warn" title="Couldn’t refresh">Showing the last numbers. Trying again in a few seconds.</Notice>}
            {board.missing && board.missing.length > 0 && (
              <Notice title="Some numbers aren’t available from your phone system yet" icon="info">
                Not shown: {board.missing.join(', ')}.
              </Notice>
            )}

            {((totals && board.queues.length > 1) || texts?.totals) && (
              <div className={`wb-strip${totals && board.queues.length > 1 && texts?.totals ? ' both' : ''}`}>
                {totals && board.queues.length > 1 && (
                  <div className="wb-totals" aria-label="All queues">
                    <Stat label="Waiting now" value={totals.waiting} tone={toneFor(totals.waiting, limits.waiting)} />
                    <Stat label="Agents ready" value={totals.ready} sub={`of ${totals.loggedIn} signed in`} />
                    <Stat label="On a call" value={totals.busy} />
                    <Stat label="Answered today" value={totals.answered} />
                    <Stat label="Hung up waiting" value={totals.missed} />
                  </div>
                )}
                {texts?.totals && <TextTotals n={texts.totals} limits={textLimits} since={since} />}
              </div>
            )}

            {signProblem && <Notice tone="crit">{signProblem}</Notice>}
            <div className="wb-queues">
              {board.queues.map((q) => (
                <QueueTile
                  key={q.extension}
                  q={q}
                  limits={limits}
                  since={since}
                  busy={signing}
                  {...(textOf.has(q.extension) ? { texts: textOf.get(q.extension)!, textLimits } : {})}
                  {...(canDndOff
                    ? {
                        dndOff: (a: { extension: string; name: string }) => (
                          <TurnOffDnd
                            domain={tenant.domain}
                            extension={a.extension}
                            name={a.name}
                            trigger={<span className="dnd-off-action">Turn off</span>}
                            onDone={() => void load()}
                          />
                        )
                      }
                    : {})}
                  {...(board.canSignIn === 'all' || board.canSignIn?.includes(q.extension)
                    ? {
                        onSign: async (agent: string, loggedIn: boolean) => {
                          setSigning(`${q.extension}|${agent}`);
                          setSignProblem('');
                          try {
                            const r = await api.queueAgent(tenant.domain, q.extension, agent, loggedIn);
                            if (r.status !== 'applied' && r.status !== 'unchanged') setSignProblem(r.message ?? 'That didn’t work. Try again.');
                            await load();
                          } catch (e) {
                            setSignProblem(e instanceof Error ? e.message : String(e));
                          } finally {
                            setSigning('');
                          }
                        }
                      }
                    : {})}
                />
              ))}
              {textOnly.map((t) => (
                <TextsTile key={t.extension} t={t} limits={textLimits} since={since} />
              ))}
            </div>

            {texts?.waiting && <TextsWaiting domain={tenant.domain} list={texts.waiting} since={since} onChanged={() => void load()} />}

            {/* "Today" for calls is the phone system's day; for texts, the company's (3136). Said when they differ. */}
            {texts && tenant.callsTimeZone && tenant.timeZone && tenant.callsTimeZone !== tenant.timeZone && (
              <p className="muted small">
                Calls “today” count from midnight {zoneName(tenant.callsTimeZone)}, the phone system’s day. Texts “today” count from midnight {zoneName(tenant.timeZone)}, your
                company’s.
              </p>
            )}

            {(board.parked.length > 0 || board.rules.length > 0) && (
              <div className="wb-side">
                {board.parked.length > 0 && (
                  <div className="wb-panel">
                    <span className="wb-panel-title">
                      <Icon name="park" size={16} /> Parked calls
                    </span>
                    {board.parked.map((p) => (
                      <span key={p.extension} className="wb-line">
                        <span>{p.name}</span>
                        <Chip small tone="warn">
                          {plural(p.calls, 'call')}
                        </Chip>
                      </span>
                    ))}
                  </div>
                )}
                {board.rules.length > 0 && (
                  <div className="wb-panel">
                    <span className="wb-panel-title">
                      <Icon name="clock" size={16} /> Time of day rules
                    </span>
                    {board.rules.map((r) => (
                      <span key={r.extension} className="wb-line">
                        <span>{r.name}</span>
                        <Chip small tone={r.on ? 'warn' : 'ok'}>
                          {r.on ? 'On' : 'Off'}
                        </Chip>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {(displays.data?.displays.length ?? 0) > 0 && (
          <Card title="TVs showing this wallboard">
            <ul className="tv-list">
              {displays.data!.displays.map((x) => (
                <TvRow key={x.id} domain={tenant.domain} display={x} onGone={displays.reload} />
              ))}
            </ul>
          </Card>
        )}
      </div>
      {tvOpen && (
        <OpenOnTv
          domain={tenant.domain}
          limits={limits}
          onClose={() => {
            setTvOpen(false);
            displays.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Where a TV's window opens: its pass after "#", so it never reaches a server or proxy log. Set up
 * from Stitch Admin's own container (staff viewing a company), it's the portal's address: the TV
 * is the company's, and Stitch Admin is only open to the Stitch team's offices.
 */
const tvUrl = (portal: string | undefined, id: string, secret: string) => `${portal ?? location.origin}/tv#${id}.${secret}`;

/**
 * Sets up a TV: a name, then its own window (drag it to the TV's screen) or a link to open on
 * another computer. Each TV gets its own read-only pass, so it keeps going after you sign out.
 */
function OpenOnTv({ domain, limits, onClose }: { domain: string; limits: Limits; onClose: () => void }) {
  const [name, setName] = useState('Wallboard TV');
  // A new TV shows each agent and plays sounds, with this page's alert levels; all can change later.
  const [choices, setChoices] = useState<DisplayOptions>({ showAgents: true, limits, sounds: { on: true, volume: 'medium', events: DEFAULT_SOUNDS } });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [made, setMade] = useState<{ url: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const { state } = useAuth();

  async function create(openWindow: boolean) {
    setBusy(true);
    setError('');
    // Opened straight away (browsers only allow a new window right after a click), then pointed at the TV page.
    const w = openWindow ? window.open('about:blank', `stitch-tv-${Date.now()}`, 'popup=yes,width=1280,height=760') : null;
    try {
      const r = await api.addWallboardDisplay(domain, name.trim() || 'Wallboard TV', choices);
      const url = tvUrl(state?.surface === 'admin' ? state.links?.portal : undefined, r.display.id, r.secret);
      if (w) {
        w.location.href = url;
        onClose();
      } else {
        if (openWindow) setError('Your browser blocked the new window. Allow pop-ups for Stitch, or use the link below.');
        setMade({ url, name: r.display.name });
      }
    } catch (e) {
      w?.close();
      setError(e instanceof Error ? e.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Show the wallboard on a TV"
      foot={
        made ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" disabled={busy} onClick={() => void create(false)}>
              Get a link instead
            </Button>
            <Button variant="primary" icon="tv" disabled={busy} onClick={() => void create(true)}>
              {busy ? 'Opening…' : 'Open in a new window'}
            </Button>
          </>
        )
      }
    >
      {made ? (
        <div className="tv-made">
          <p>
            Open this link on the computer connected to the TV. It shows <strong>{made.name}</strong> without signing in, and keeps going until you turn it off here.
          </p>
          <div className="tv-link">
            <input className="input mono" readOnly value={made.url} aria-label="Link for the TV" onFocus={(e) => e.target.select()} data-private="" />
            <Button
              icon="copy"
              onClick={() => {
                void navigator.clipboard?.writeText(made.url).then(() => setCopied(true), () => undefined);
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="muted small">Anyone with this link can see the wallboard, so only open it on the TV. It isn’t shown again.</p>
          {error && <Notice tone="warn">{error}</Notice>}
        </div>
      ) : (
        <div className="tv-setup">
          <p>
            The wallboard opens in its own window, in big type. Drag that window onto the TV’s screen and choose <strong>Full screen</strong>.
          </p>
          <ul className="tv-points">
            <li>It keeps showing after you sign out or close Stitch, and never needs signing in again.</li>
            <li>It can only show the wallboard: nobody can change anything from it.</li>
            <li>Turn it off any time under <strong>TVs showing this wallboard</strong>, below the board.</li>
          </ul>
          <Field label="Name this screen" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} hint="So you can tell your TVs apart, like “Sales floor TV”." />
          <TvChoices value={choices} onChange={setChoices} />
          {error && <Notice tone="crit">{error}</Notice>}
        </div>
      )}
    </Dialog>
  );
}

function TvRow({ domain, display, onGone }: { domain: string; display: WallboardDisplay; onGone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const o = display.options;
  const seen = display.lastSeenAt ? Date.now() - Date.parse(display.lastSeenAt) : null;
  const on = seen !== null && seen < 3 * 60_000;
  return (
    <li className="tv-row">
      <span className={`dot ${on ? 'on' : ''}`} aria-hidden="true" />
      <span className="cell-stack grow">
        <strong>{display.name}</strong>
        <small className="muted">
          {on ? 'Showing now' : display.lastSeenAt ? `Last showing ${shortWhen(display.lastSeenAt)}` : 'Not opened yet'} · set up by {display.createdByName}
          {display.queues !== 'all' ? ` · ${plural(display.queues.length, 'queue')}` : ''}
        </small>
        <small className="muted">
          {o.showAgents ? 'Shows each agent' : 'Agent details hidden'} · {o.sounds.on ? `Sounds on (${VOLUMES.find((v) => v.id === o.sounds.volume)?.label.toLowerCase() ?? 'medium'})` : 'Sounds off'}
        </small>
        {error && <small className="crit-text">{error}</small>}
      </span>
      <Button size="small" variant="ghost" icon="sliders" disabled={busy} onClick={() => setEditing(true)} aria-label={`Settings for ${display.name}`}>
        Settings
      </Button>
      {editing && (
        <TvSettings
          domain={domain}
          display={display}
          onClose={(changed) => {
            setEditing(false);
            if (changed) onGone();
          }}
        />
      )}
      {/* Asks first (3138): a TV turned off needs setting up again on the TV itself. */}
      <Button size="small" variant="ghost" disabled={busy} onClick={() => setConfirming(true)}>
        Turn off
      </Button>
      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Turn off ${display.name}?`}
        foot={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError('');
                try {
                  await api.removeWallboardDisplay(domain, display.id);
                  setConfirming(false);
                  onGone();
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'That didn’t work.');
                  setConfirming(false);
                  setBusy(false);
                }
              }}
            >
              {busy ? 'Turning off…' : 'Turn off'}
            </Button>
          </>
        }
      >
        <p>It stops showing the wallboard straight away and says it was turned off. To show the board there again, set it up again from this page.</p>
      </Dialog>
    </li>
  );
}

/**
 * The numbers across all queues. Callers and calls add up; agents don't: someone in three queues is
 * one agent (each counted once, by extension).
 */
export function boardTotals(queues: readonly BoardQueue[]) {
  const agents = agentTotals(queues);
  return {
    waiting: queues.reduce((n, q) => n + q.waiting, 0),
    ready: agents.ready,
    busy: agents.busy,
    loggedIn: agents.loggedIn,
    answered: queues.reduce((n, q) => n + (q.answeredToday ?? 0), 0),
    missed: queues.reduce((n, q) => n + (q.abandonedToday ?? 0), 0)
  };
}

export function Stat({ label, value, tone = 'calm', sub }: { label: string; value: string | number; tone?: 'calm' | 'warn' | 'crit'; sub?: string }) {
  return (
    <div className={`wb-stat ${tone}`}>
      <span className="wb-stat-value">{value}</span>
      <span className="wb-stat-label">{label}</span>
      {sub && <span className="wb-stat-sub">{sub}</span>}
    </div>
  );
}

const STATE: Record<'available' | 'busy' | 'dnd' | 'loggedOut', { label: string; cls: string }> = {
  available: { label: 'Ready', cls: 'ready' },
  busy: { label: 'On a call', cls: 'busy' },
  dnd: { label: 'Do not disturb', cls: 'dnd' },
  loggedOut: { label: 'Signed out', cls: 'out' }
};

export function QueueTile({
  q,
  limits,
  since,
  onSign,
  dndOff,
  busy,
  showAgents = true,
  compact = false,
  texts,
  textLimits
}: {
  q: BoardQueue;
  limits: Limits;
  since: number;
  onSign?: (agent: string, loggedIn: boolean) => Promise<void>;
  /** Admins: a way to turn off an agent's do not disturb (shown on agents in DND). */
  dndOff?: (agent: { extension: string; name: string }) => ReactNode;
  busy?: string;
  /** Each agent's name and state (a TV can hide them: the counts stay). */
  showAgents?: boolean;
  /** A TV with more queues than fit (1.12.13): the four main boxes, and agents counted by state. */
  compact?: boolean;
  /** The queue takes texts (1.12.32): its texts under its calls, and each agent's. */
  texts?: TextTeamBoard;
  textLimits?: TextLimits;
}) {
  // Waits grow between refreshes, so the board never looks frozen.
  const longest = q.longestWaitSeconds !== undefined && q.waiting > 0 ? q.longestWaitSeconds + since : undefined;
  const ready = q.agents.filter((a) => a.state === 'available').length;
  const loggedIn = q.agents.filter((a) => a.state !== 'loggedOut').length;
  const waitTone = toneFor(q.waiting, limits.waiting);
  const longTone = longest === undefined ? 'calm' : toneFor(longest, limits.waitSeconds);
  const noOne = q.waiting > 0 && ready === 0;
  const textTone = texts ? textTones(texts, textLimits ?? loadTextLimits(), since).band : 'calm';
  const tones = [waitTone, longTone, textTone];
  const textOf = new Map((texts?.agents ?? []).map((a) => [a.extension, a]));
  return (
    <section className={`wb-queue ${tones.includes('crit') ? 'crit' : tones.includes('warn') ? 'warn' : ''}`} aria-label={`${q.name} queue`}>
      <header className="wb-queue-head">
        <h2>{q.name}</h2>
        <span className="muted">
          Queue {q.extension}
          {texts && ` · ${handoutWords(texts)}`}
        </span>
      </header>
      {texts && !compact && (
        <span className="wb-band-title">
          <Icon name="phone" size={14} /> Calls
        </span>
      )}
      <div className="wb-stats">
        <Stat label="Waiting" value={q.waiting} tone={waitTone} />
        <Stat label="Longest wait" value={longest === undefined ? (q.waiting ? '–' : '0:00') : mmss(longest)} tone={longTone} />
        <Stat label="Ready" value={`${ready}`} sub={`of ${loggedIn} signed in`} tone={noOne ? 'crit' : 'calm'} />
        <Stat label="Answered today" value={q.answeredToday ?? '–'} sub={q.callsToday !== undefined && !compact ? `of ${q.callsToday} calls` : undefined} />
        {!compact && <Stat label="Hung up waiting" value={q.abandonedToday ?? '–'} />}
        {!compact && q.avgWaitSeconds !== undefined && <Stat label="Average wait" value={mmss(q.avgWaitSeconds)} sub={q.maxWaitSeconds ? `longest ${mmss(q.maxWaitSeconds)}` : undefined} />}
        {!compact && q.avgTalkSeconds !== undefined && <Stat label="Average call" value={mmss(q.avgTalkSeconds)} sub={q.missedToday ? `${q.missedToday} missed by agents` : undefined} />}
      </div>
      {noOne && (
        <p className="wb-alert">
          <Icon name="alert" size={15} /> Callers are waiting and nobody is ready.
        </p>
      )}
      {texts && <TextStats t={texts} limits={textLimits ?? loadTextLimits()} since={since} compact={compact} />}
      {compact ? (
        <AgentCounts agents={q.agents} />
      ) : showAgents && q.agents.length > 0 && (
        <ul className="wb-agents" aria-label="Agents">
          {q.agents.map((a) => {
            const talk = q.talking?.find((t) => t.agent === a.extension);
            return (
              <li key={a.extension} className={`wb-agent ${STATE[a.state].cls}`}>
                <span className="dot" aria-hidden="true" />
                <span className="wb-agent-name">{a.name}</span>
                <span className="wb-agent-state">{talk ? `On a call · ${mmss(talk.seconds + since)}` : STATE[a.state].label}</span>
                {a.state === 'dnd' && dndOff && <span className="wb-sign">{dndOff(a)}</span>}
                {a.answeredToday !== undefined && <span className="wb-agent-count">{a.answeredToday}</span>}
                {onSign && (
                  <button
                    type="button"
                    className="link-btn wb-sign"
                    disabled={busy === `${q.extension}|${a.extension}`}
                    onClick={() => void onSign(a.extension, a.state === 'loggedOut')}
                    aria-label={`${a.state === 'loggedOut' ? 'Sign in' : 'Sign out'} ${a.name} ${a.state === 'loggedOut' ? 'to' : 'of'} ${q.name}`}
                  >
                    {a.state === 'loggedOut' ? 'Sign in' : 'Sign out'}
                  </button>
                )}
                <AgentTexts a={textOf.get(a.extension)} />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** "2 on a call · 1 ready": a compact tile's agents, counted by how they are (never names). */
function AgentCounts({ agents }: { agents: BoardQueue['agents'] }) {
  if (!agents.length) return <p className="wb-sum muted">Nobody in this queue</p>;
  const order = ['busy', 'available', 'dnd', 'loggedOut'] as const;
  const words = { busy: 'on a call', available: 'ready', dnd: 'do not disturb', loggedOut: 'signed out' };
  return (
    <p className="wb-sum" aria-label="Agents">
      {order
        .map((st) => [st, agents.filter((a) => a.state === st).length] as const)
        .filter(([, n]) => n > 0)
        .map(([st, n]) => (
          <span key={st} className={`wb-agent ${STATE[st].cls}`}>
            <span className="dot" aria-hidden="true" />
            {n} {words[st]}
          </span>
        ))}
    </p>
  );
}

// --- texts (1.12.32) -----------------------------------------------------------------------------------

const handoutWords = (t: Pick<TextTeamBoard, 'handout' | 'inHours'>) => `texts ${t.handout === 'one' ? 'one at a time' : 'to everyone'}${t.inHours ? '' : ' · closed now'}`;

/** The texts across every team on the board: waiting (and the oldest), open, answered today. */
export function TextTotals({ n, limits, since }: { n: TextNumbers; limits: TextLimits; since: number }) {
  const oldest = oldestNow(n, since);
  return (
    <div className="wb-totals wb-text-totals" aria-label="Texts, all teams">
      <Stat label="Texts waiting" value={n.waiting} tone={textTones(n, limits, since).band} sub={oldest !== null ? `oldest ${mmss(oldest)}` : undefined} />
      <Stat label="Open texts" value={n.open} />
      <Stat label="Texts answered today" value={n.answeredToday} sub={n.avgFirstReplySeconds !== null ? `first reply ${mmss(n.avgFirstReplySeconds)} average` : undefined} />
    </div>
  );
}

/** A team's texts: waiting, the oldest, open, answered today (a compact TV tile: one line). */
export function TextStats({ t, limits, since, compact = false }: { t: TextTeamBoard; limits: TextLimits; since: number; compact?: boolean }) {
  const oldest = oldestNow(t, since);
  const tones = textTones(t, limits, since);
  if (compact)
    return (
      <p className={`wb-sum wb-text-sum ${tones.band}`} aria-label="Texts">
        <Icon name="chat" size={14} />
        <span>
          <b>{t.waiting}</b> waiting{oldest !== null && ` · oldest ${mmss(oldest)}`}
        </span>
        <span>
          <b>{t.open}</b> open
        </span>
        <span>
          <b>{t.answeredToday}</b> answered
        </span>
      </p>
    );
  const sub = t.urgent ? `${t.urgent} urgent` : t.inLine > 0 && t.inLine < t.waiting ? `${t.inLine} with nobody yet` : undefined;
  return (
    <>
      <span className="wb-band-title">
        <Icon name="chat" size={14} /> Texts
      </span>
      <div className="wb-stats">
        <Stat label="Waiting" value={t.waiting} tone={tones.waiting} sub={sub} />
        <Stat label="Oldest text" value={oldest === null ? '0:00' : mmss(oldest)} tone={tones.oldest} />
        <Stat label="Open" value={t.open} />
        <Stat label="Answered today" value={t.answeredToday} sub={t.avgFirstReplySeconds !== null ? `first reply ${mmss(t.avgFirstReplySeconds)}` : undefined} />
      </div>
    </>
  );
}

/** "3 texts · texts paused", under an agent's name and state. */
function AgentTexts({ a }: { a: Pick<TextAgent, 'openTexts' | 'paused'> | undefined }) {
  const words = agentTextWords(a);
  if (!words) return null;
  return (
    <span className="wb-agent-texts">
      <Icon name="chat" size={13} />
      {words}
    </span>
  );
}

/**
 * A ring group's texts (or a queue's the board didn't list), in a tile of its own: ring groups'
 * calls aren't counted on the wallboard, so only their texts are.
 */
export function TextsTile({ t, limits, since, showAgents = true, compact = false }: { t: TextTeamBoard; limits: TextLimits; since: number; showAgents?: boolean; compact?: boolean }) {
  const tone = textTones(t, limits, since).band;
  const ring = t.kind === 'ringGroup';
  return (
    <section className={`wb-queue wb-texts-only ${tone === 'calm' ? '' : tone}`} aria-label={`${t.name} ${ring ? 'ring group' : 'queue'}`}>
      <header className="wb-queue-head">
        <h2>{t.name}</h2>
        <span className="muted">
          {ring ? 'Ring group' : 'Queue'} {t.extension} · {handoutWords(t)}
        </span>
      </header>
      <TextStats t={t} limits={limits} since={since} compact={compact} />
      {!compact && ring && <p className="wb-note">Calls to ring groups aren’t counted here, only their texts.</p>}
      {showAgents && !compact && t.agents.length > 0 && (
        <ul className="wb-agents" aria-label="People">
          {t.agents.map((a) => {
            const state = a.onCall ? { cls: 'busy', label: 'On a call' } : a.here ? { cls: 'ready', label: 'Available' } : { cls: 'out', label: 'Away' };
            return (
              <li key={a.extension || a.name} className={`wb-agent ${state.cls}`}>
                <span className="dot" aria-hidden="true" />
                <span className="wb-agent-name">{a.name}</span>
                <span className="wb-agent-state">{state.label}</span>
                <AgentTexts a={a} />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Texts waiting for a reply, for the teams' supervisors: who (their number, or the name the company
 * gave it), how long, who has it, and Open or Hand to. Never on a TV.
 */
function TextsWaiting({ domain, list, since, onChanged }: { domain: string; list: WaitingText[]; since: number; onChanged: () => void }) {
  const [handing, setHanding] = useState<WaitingText | null>(null);
  return (
    <Card title="Texts waiting for a reply" action={<span className="muted small">Supervisors only. TVs show the numbers above, never who texted.</span>}>
      {list.length === 0 ? (
        <Empty title="Nobody is waiting for a reply">Texts show here, oldest first, until someone answers them.</Empty>
      ) : (
        <ul className="wb-waiting">
          {list.map((w) => {
            const secs = w.waitingSeconds + since;
            return (
              <li key={w.conversationId} className={w.urgent ? 'urgent' : undefined}>
                <span className="cell-stack grow">
                  <strong>
                    {w.name ? `${w.name} · ${w.number}` : w.number}
                    {w.urgent && (
                      <>
                        {' '}
                        <Chip small tone="crit">
                          Urgent
                        </Chip>
                      </>
                    )}
                  </strong>
                  <small className="muted">{w.teamName}</small>
                </span>
                <span className="wb-waiting-who">{w.owner ? `With ${w.owner.name}` : 'Nobody yet'}</span>
                <span className="wb-waiting-time" title="Waiting for a reply">
                  {mmss(secs)}
                </span>
                <span className="wb-waiting-actions">
                  {w.canOpen && <Link to={`../messages/${w.conversationId}`}>Open</Link>}
                  {w.canHand && (
                    <Button size="small" variant="ghost" onClick={() => setHanding(w)}>
                      Hand to…
                    </Button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {handing && (
        <HandTo
          domain={domain}
          w={handing}
          onClose={(done) => {
            setHanding(null);
            if (done) onChanged();
          }}
        />
      )}
    </Card>
  );
}

/** Hand a waiting text to someone on its team (or a supervisor): the people Messages offers. */
function HandTo({ domain, w, onClose }: { domain: string; w: WaitingText; onClose: (done: boolean) => void }) {
  const people = useData(() => api.handTargets(domain, w.conversationId), [domain, w.conversationId]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  return (
    <Dialog open onClose={() => onClose(false)} title={`Hand ${w.name ?? w.number} to…`} subtitle={`${w.teamName}. They’re told, and it’s theirs to answer.`}>
      {people.error ? (
        <LoadError error={people.error} retry={people.reload} />
      ) : !people.data ? (
        <Loading label="Loading people" />
      ) : people.data.people.length === 0 ? (
        <Empty title="Nobody to hand it to">Nobody on {w.teamName} uses Stitch yet.</Empty>
      ) : (
        <ul className="plain-list wb-hand-list">
          {people.data.people
            .filter((p) => p.id !== w.owner?.id)
            .map((p) => (
              <li key={p.id}>
                <span className="cell-stack grow">
                  <strong>{p.name}</strong>
                  <small className="muted">{handToWords(p, w.teamName)}</small>
                </span>
                <Button
                  size="small"
                  variant={p.here && p.signedIn ? 'primary' : 'secondary'}
                  disabled={Boolean(busy)}
                  onClick={async () => {
                    setBusy(p.id);
                    setError('');
                    try {
                      await api.handText(domain, w.conversationId, p.id);
                      onClose(true);
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'That didn’t work.');
                      setBusy('');
                    }
                  }}
                >
                  {busy === p.id ? 'Handing…' : 'Hand to'}
                </Button>
              </li>
            ))}
        </ul>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

/** A TV's choices: whether it shows each agent, its alert levels, and its sounds. */
function TvChoices({ value, onChange }: { value: DisplayOptions; onChange: (v: DisplayOptions) => void }) {
  const set = (next: Partial<DisplayOptions>) => onChange({ ...value, ...next });
  const sounds = (next: Partial<DisplayOptions['sounds']>) => onChange({ ...value, sounds: { ...value.sounds, ...next } });
  const limits = value.limits ?? { waiting: 3, waitSeconds: 120 };
  return (
    <div className="tv-choices">
      <label className="check-row">
        <input type="checkbox" checked={value.showAgents} onChange={(e) => set({ showAgents: e.target.checked })} />
        <span className="cell-stack">
          <span>Show each agent</span>
          <small className="muted">
            {value.showAgents
              ? 'Names, who is ready or on a call (and for how long), and calls each has answered today.'
              : 'Off: the TV shows how many agents are ready, but no names. Their details don’t reach the TV at all.'}
          </small>
        </span>
      </label>
      <div className="tv-limits">
        <label className="field">
          <span className="field-label">Amber at callers waiting</span>
          <select className="input" value={limits.waiting} onChange={(e) => set({ limits: { ...limits, waiting: Number(e.target.value) } })}>
            {LIMIT_WAITING.map((n) => (
              <option key={n} value={n}>
                {n} or more
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Amber at longest wait</span>
          <select className="input" value={limits.waitSeconds} onChange={(e) => set({ limits: { ...limits, waitSeconds: Number(e.target.value) } })}>
            {LIMIT_SECONDS.map((n) => (
              <option key={n} value={n}>
                {mmss(n)} or longer
              </option>
            ))}
          </select>
        </label>
        <span className="hint">Red at twice that.</span>
      </div>
      <label className="check-row">
        <input type="checkbox" checked={value.sounds.on} onChange={(e) => sounds({ on: e.target.checked })} />
        <span className="cell-stack">
          <span>Play sounds</span>
          <small className="muted">A different sound for each thing below. The TV needs one click (or Full screen) before it can play sound.</small>
        </span>
      </label>
      {value.sounds.on && (
        <div className="tv-sounds">
          <label className="field">
            <span className="field-label">Volume</span>
            <select className="input" value={value.sounds.volume} onChange={(e) => sounds({ volume: e.target.value as SoundVolume })}>
              {VOLUMES.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          <ul className="plain-list tv-sound-list" aria-label="Sounds">
            {BOARD_SOUNDS.map((x) => (
              <li key={x.id}>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={value.sounds.events.includes(x.id)}
                    onChange={(e) =>
                      sounds({ events: e.target.checked ? BOARD_SOUNDS.map((b) => b.id).filter((id) => id === x.id || value.sounds.events.includes(id)) : value.sounds.events.filter((id) => id !== x.id) })
                    }
                  />
                  <span className="cell-stack">
                    <span>{x.label}</span>
                    <small className="muted">{x.hint}</small>
                  </span>
                </label>
                <button type="button" className="link-btn" onClick={() => void previewSound(x.id, value.sounds.volume)} aria-label={`Play: ${x.label}`}>
                  <Icon name="play" size={14} /> Play
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** A TV's name and choices, changed from here: the TV picks them up within seconds. */
function TvSettings({ domain, display, onClose }: { domain: string; display: WallboardDisplay; onClose: (changed: boolean) => void }) {
  const [name, setName] = useState(display.name);
  const [choices, setChoices] = useState<DisplayOptions>({ ...display.options, limits: display.options.limits ?? loadLimits() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function save() {
    setBusy(true);
    setError('');
    try {
      await api.changeWallboardDisplay(domain, display.id, { name: name.trim() || display.name, options: choices });
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That didn’t save.');
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onClose={() => onClose(false)}
      title={`Settings for ${display.name}`}
      subtitle="The TV picks these up within a few seconds."
      foot={
        <>
          <Button variant="ghost" disabled={busy} onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="tv-setup">
        <Field label="Name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        <TvChoices value={choices} onChange={setChoices} />
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );
}
